/**
 * THE REPLAY PROGRESSION GATE — take a trade in a replay and you walk it out, bar by bar.
 *
 * <h2>The discrepancy this closes</h2>
 *
 * <p>In replay mode the bars after the playhead are not drawn, so a trade taken at a paused CTS
 * point can only ever be OPENED there: its target, its stop and its ten-bar horizon all live in
 * bars the reader has not been shown. Nothing stopped them jumping the playhead thirty bars to
 * the next timing point and reading the outcome as a fait accompli — which is reading a result
 * rather than experiencing one, and is the one thing a replay is for.
 *
 * <p>So while a taken trade is unresolved, the playhead may advance by exactly ONE BAR at a
 * time. Once the trade resolves — target, stop, or the horizon elapsing — the gate drops and
 * every ordinary gesture is restored, including the multi-bar jump to the next CTS point.
 *
 * <h2>What arms it, and what deliberately does not</h2>
 *
 * <ul>
 *   <li><b>A RULED trade that is OPEN at the boundary.</b> The brief's resolution criteria are
 *       "the ATR target/stop or the max 10-bar forward horizon", which are the CTS rule's, so
 *       the gate watches the rule's own verdict rather than inventing a second one.</li>
 *   <li><b>Only in a replay.</b> Outside one the boundary is null, every bar is drawn, and a
 *       trade resolves the instant it is taken. There is nothing to walk out.</li>
 *   <li><b>NOT a point well behind the playhead.</b> Clicking a 1998 marker on a chart revealed
 *       to 2008 prices a trade that is already resolved, so it arms nothing. Only the point the
 *       reader is actually paused on gates the chart — which is what keeps browsing the ledger
 *       from becoming a punishment.</li>
 *   <li><b>NOT a manual two-click trade.</b> Those have no target and no stop, so "resolved"
 *       has no meaning for them, and their legs are already bounded by the same boundary.</li>
 * </ul>
 *
 * <h2>It is a DISCIPLINE, not a lock</h2>
 *
 * <p>`release` exists and the card shows it. A reader who does not want to walk a trade out must
 * have a way out — an invisible constraint with no escape reads as a broken chart rather than as
 * a feature, and the gate's value is in making the jump a deliberate act rather than in making
 * it impossible. Releasing leaves the trade OPEN in the ledger, which is the honest record.
 */

import { BAR_HIGH, BAR_LOW } from './analyticaGeometry';
import { OUTCOMES } from './tradeSimEngine';

const n = (v) => (Number.isFinite(v) ? v : null);

/** How far the playhead may advance in one gesture while a trade is being walked out. */
export const GATED_STEP = 1;

/**
 * The gate a trade arms, or null.
 *
 * @param {object} trade    a `simulateTrade` result plus `provider` and `date`
 * @param {number} boundary the replay playhead, or null outside a replay
 */
export function gateFor(trade, boundary) {
  if (!trade || !Number.isFinite(boundary)) return null;
  // UNRESOLVED ONLY. A trade the engine has already settled has nothing left to walk.
  if (!trade.priced || trade.outcome !== OUTCOMES.OPEN) return null;
  const entryIndex = Math.floor(n(trade.index) ?? -1);
  const horizon = Math.floor(n(trade.horizon) ?? 0);
  const target = n(trade.target);
  const stop = n(trade.stop);
  // A RULED trade. Without a target and a stop the only resolution left would be the horizon,
  // which is a timer rather than a trade being walked out.
  if (entryIndex < 0 || horizon <= 0 || target === null || stop === null) return null;
  return {
    provider: trade.provider || null,
    date: trade.date || null,
    id: trade.id || null,
    entryIndex,
    horizonIndex: entryIndex + horizon,
    target,
    stop,
    sign: trade.direction === 'down' ? -1 : 1,
    /*
     * THE RULES THE TRADE WAS PRICED WITH, carried rather than assumed.
     *
     * The caller re-prices through `simulateTrade` on every bar the playhead advances, and a
     * literal 1-ATR target there would be a SECOND statement of the CTS rule — correct today
     * and silently wrong for the first provider whose multiples differ, with the symptom being
     * a ledger entry that disagrees with the card it was opened from.
     */
    rules: {
      horizon,
      targetAtr: n(trade.targetAtr) ?? 1,
      stopAtr: n(trade.stopAtr) ?? 1,
    },
  };
}

/**
 * Has the gated trade resolved by the time the playhead reaches `boundary`?
 *
 * <p>THE SAME FILL RULES THE ENGINE USES, deliberately restated as a question rather than
 * recomputed as a price: the stop is tested before the target so a bar reaching both resolves
 * adversely, and the walk starts at the bar AFTER the entry. What it must NOT do is re-derive
 * the P&L — that is `simulateTrade`'s, and the caller re-prices through it so the ledger and the
 * gate can never disagree about what happened.
 *
 * @returns {{resolved: boolean, atIndex: number|null, reason: string|null}}
 */
export function gateResolution(gate, ohlc, boundary) {
  if (!gate) return { resolved: true, atIndex: null, reason: null };
  const bars = Array.isArray(ohlc) ? ohlc : [];
  const edge = Number.isFinite(boundary)
    ? Math.min(Math.floor(boundary), bars.length - 1)
    : bars.length - 1;

  for (let k = gate.entryIndex + 1; k <= Math.min(gate.horizonIndex, edge); k++) {
    const high = n(bars[k]?.[BAR_HIGH]);
    const low = n(bars[k]?.[BAR_LOW]);
    if (high === null || low === null) continue;
    const hitStop = gate.sign > 0 ? low <= gate.stop : high >= gate.stop;
    if (hitStop) return { resolved: true, atIndex: k, reason: 'stop hit' };
    const hitTarget = gate.sign > 0 ? high >= gate.target : low <= gate.target;
    if (hitTarget) return { resolved: true, atIndex: k, reason: 'target reached' };
  }
  if (edge >= gate.horizonIndex) {
    return { resolved: true, atIndex: gate.horizonIndex, reason: 'horizon expired' };
  }
  return { resolved: false, atIndex: null, reason: null };
}

/** Bars still to walk before the horizon can elapse. Zero once the playhead is past it. */
export function barsRemaining(gate, boundary) {
  if (!gate || !Number.isFinite(boundary)) return 0;
  return Math.max(0, gate.horizonIndex - Math.floor(boundary));
}

/**
 * The reveal index a gesture is allowed to reach.
 *
 * <p>FORWARD ONLY, and that is the whole clamp: a request to move backwards reveals nothing, so
 * it cannot defeat a gate whose purpose is to make the reader see each bar as it arrives, and
 * refusing it would take the arrow keys' one deliberate rewind away for no gain. A request to
 * stand still is returned unchanged for the same reason.
 *
 * @param gate      the armed gate, or null for no constraint
 * @param from      the boundary now
 * @param requested where the gesture wants to go
 * @returns {number} the index to reveal to
 */
export function clampAdvance(gate, from, requested) {
  const want = Math.floor(n(requested) ?? 0);
  if (!gate || !Number.isFinite(from)) return want;
  const here = Math.floor(from);
  if (want <= here) return want;
  return Math.min(want, here + GATED_STEP);
}

/** True when a gesture asked for more than the gate allowed, so the caller can say so. */
export const wasClamped = (gate, from, requested) =>
  clampAdvance(gate, from, requested) !== Math.floor(n(requested) ?? 0);

export default gateFor;
